import { useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import { LogEntry } from '../../interface/logEntry';
import {
  StyledTableCell,
  StyledTableCellFooter,
  StyledTableRow as BaseStyledTableRow
} from './shared/StyledTableComponents';

interface TimeLogTableProps {
  logs: LogEntry[];
  page: number;
  rowsPerPage: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Extended StyledTableRow with separator styling for TimeLogTable
 * Adds a thick black border between different days
 */
const StyledTableRow = styled(BaseStyledTableRow)(({ theme }) => ({
  "&.separator-row": {
    borderBottom: `4px solid ${theme.palette.table.separator}`,
  }
}));

/**
 * Extended StyledTableCell with TimeLogTable-specific styles
 * - First column (date) has nowrap
 * - Description column has custom width and wrapping
 */
const TimeLogTableCell = styled(StyledTableCell)({
  '&:first-of-type': {
    whiteSpace: "nowrap",
  },
  '&.desc': {
    maxWidth: "600px",
    whiteSpace: "normal",
    width: "70%"
  },
});

const PeachTotalCell = styled(StyledTableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: theme.palette.table.totalHighlight,
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
}));

const TimeLogTable = ({ logs, page, rowsPerPage, onPageChange, onRowsPerPageChange }: TimeLogTableProps) => {
  // Filter out weekends and sort by date
  const processedLogs = useMemo(() => {
    return logs
      .filter((row) => {
        const dayOfWeek = new Date(row.date).getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6; // Exclude weekends
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [logs]);

  // Calculate total hours from all logs (in minutes, convert to hours)
  const totalHours = useMemo(() => {
    const totalMinutes = processedLogs.reduce((sum, log) => {
      return sum + (typeof log.duration === 'number' ? log.duration : 0);
    }, 0);
    return totalMinutes;
  }, [processedLogs]);

  // Paginated logs
  const paginatedLogs = useMemo(() => {
    return processedLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [processedLogs, page, rowsPerPage]);

  // Calculate hours per day for display
  const logsWithDailyHours = useMemo(() => {
    const result: Array<{
      log: LogEntry;
      isLastOfDay: boolean;
      dailyHours: number;
    }> = [];

    let currentDayTotal = 0;

    paginatedLogs.forEach((log, index) => {
      const currentDuration = typeof log.duration === 'number' ? log.duration : 0;
      currentDayTotal += currentDuration;

      const nextLog = paginatedLogs[index + 1];
      const isLastOfDay = nextLog ? nextLog.date !== log.date : false;

      result.push({
        log,
        isLastOfDay,
        dailyHours: isLastOfDay ? currentDayTotal / 60 : 0,
      });

      if (isLastOfDay) {
        currentDayTotal = 0;
      }
    });

    return result;
  }, [paginatedLogs]);

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="time log table">
          <TableHead>
            <TableRow>
              <TimeLogTableCell>Date</TimeLogTableCell>
              <TimeLogTableCell>Client</TimeLogTableCell>
              <TimeLogTableCell>Description</TimeLogTableCell>
              <TimeLogTableCell align="right">Duration</TimeLogTableCell>
              <TimeLogTableCell align="right">Hours per day</TimeLogTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logsWithDailyHours.map(({ log, isLastOfDay, dailyHours }) => (
              <StyledTableRow
                className={isLastOfDay ? "separator-row" : ""}
                key={log.id}
              >
                <TimeLogTableCell>{log.date}</TimeLogTableCell>
                <TimeLogTableCell>
                  {log.client_name ?? (log.client_id ? String(log.client_id) : '—')}
                </TimeLogTableCell>
                <TimeLogTableCell className='desc'>{log.description}</TimeLogTableCell>
                <TimeLogTableCell align="right">{log.duration} min</TimeLogTableCell>
                {isLastOfDay ? (
                  <TimeLogTableCell align="right">{dailyHours.toFixed(2)}h</TimeLogTableCell>
                ) : (
                  <TimeLogTableCell align="right"></TimeLogTableCell>
                )}
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCellFooter colSpan={4} align="right">
                Total hours:
              </StyledTableCellFooter>
              <PeachTotalCell align="right">{(totalHours / 60).toFixed(1)}</PeachTotalCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50, 100, 250]}
        component="div"
        count={processedLogs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
};

export default TimeLogTable;

